import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useTranslation('forms');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: 'general',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200) {
        toast.success('Thank you! We\'ll get back to you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiryType: 'general',
          message: ''
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Contact form error:', error);
      }
      
      if (error.response?.status === 500) {
        toast.error('Failed to send inquiry. Please try again or email us directly at contact@mielamaison.com');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-[#F6F1EA]">
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1664711942326-2c3351e215e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsfGVufDB8fHx8MTc4MTc2MzA5MXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="border border-[#E8DDD0] hover:border-[#B08D57] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#B08D57]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#3A312A] mb-2">{t('cards.email.title')}</h3>
                <p className="text-[#3A312A]/70 mb-4">{t('cards.email.description')}</p>
                <a
                  href="mailto:contact@mielamaison.com"
                  className="text-[#B08D57] hover:text-[#8A6A3B] font-medium transition-colors"
                >
                  contact@mielamaison.com
                </a>
              </CardContent>
            </Card>

            <Card className="border border-[#E8DDD0] hover:border-[#B08D57] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#B08D57]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#3A312A] mb-2">{t('cards.phone.title')}</h3>
                <p className="text-[#3A312A]/70 mb-4">{t('cards.phone.hours')}</p>
                <a
                  href="tel:+818097770778"
                  className="text-[#B08D57] hover:text-[#8A6A3B] font-medium transition-colors"
                >
                  +81 (080) 9777-0778
                </a>
              </CardContent>
            </Card>

            <Card className="border border-[#E8DDD0] hover:border-[#B08D57] transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#B08D57]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#3A312A] mb-2">{t('cards.visit.title')}</h3>
                <p className="text-[#3A312A]/70 mb-4">{t('cards.visit.appointment')}</p>
                <p className="text-[#B08D57] font-medium">Osaka, Japan</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('contact.sendMessage')}
            </h2>
            <p className="text-lg text-[#3A312A]/70">
              {t('contact.description')}
            </p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#3A312A] font-medium">
                      {t('form.fullName')} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-[#E8DDD0] focus:border-[#B08D57] transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#3A312A] font-medium">
                      {t('form.email')} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-[#E8DDD0] focus:border-[#B08D57] transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-[#3A312A] font-medium">
                    {t('form.company')}
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="border-[#E8DDD0] focus:border-[#B08D57] transition-colors"
                    placeholder="Your company name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inquiryType" className="text-[#3A312A] font-medium">
                    {t('form.inquiryType')} *
                  </Label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E8DDD0] rounded-md px-3 py-2 focus:border-[#B08D57] focus:outline-none focus:ring-2 focus:ring-[#B08D57]/20 transition-colors bg-white text-[#3A312A]"
                  >
                    <option value="general">{t('inquiryTypes.general')}</option>
                    <option value="wholesale">{t('inquiryTypes.wholesale')}</option>
                    <option value="partnership">{t('inquiryTypes.partnership')}</option>
                    <option value="product">{t('inquiryTypes.product')}</option>
                    <option value="supplier">{t('inquiryTypes.supplier')}</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#3A312A] font-medium">
                    {t('form.message')} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="border-[#E8DDD0] focus:border-[#B08D57] transition-colors resize-none"
                    placeholder="Tell us about your inquiry..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#B08D57] hover:bg-[#8A6A3B] text-white py-6 text-lg font-medium transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2" size={20} />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* B2B CTA */}
      <section className="py-20 px-6 bg-[#3A312A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            {t('partnership.title')}
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            {t('partnership.description')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-2xl font-bold text-[#B08D57] mb-2">{t('partnership.features.moq.title')}</p>
              <p className="text-sm text-white/80">{t('partnership.features.moq.description')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-2xl font-bold text-[#B08D57] mb-2">{t('partnership.features.label.title')}</p>
              <p className="text-sm text-white/80">{t('partnership.features.label.description')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-2xl font-bold text-[#B08D57] mb-2">{t('partnership.features.logistics.title')}</p>
              <p className="text-sm text-white/80">{t('partnership.features.logistics.description')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
              <p className="text-2xl font-bold text-[#B08D57] mb-2">{t('partnership.features.quality.title')}</p>
              <p className="text-sm text-white/80">{t('partnership.features.quality.description')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};